import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Folder, Upload } from '@/assets/icons'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { ADD_FILES_MODAL_TAB, type AddFilesModalInitialTab } from '@/chat-common/components/source-dialogs/AddFilesModal'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { AuthContext } from '@/context/auth-context'
import { matterPaths } from '@/routing/paths'
import { ExternalStorageMenuItems } from '@/user_files/external-storage/components/ExternalStorageMenuItems'
import { FilePickerSecurityFooter } from '@/user_files/external-storage/components/FilePickerSecurityFooter'
import { useExternalStorageMenuStatus } from '@/user_files/external-storage/hooks/useExternalStorageMenuStatus'

type MatterAssetFileSourceMenuProps = {
  matterId: string | null | undefined
  onUpload: () => void
  onOpenFilePicker: (tab: AddFilesModalInitialTab) => void
}

/** The same upload, matter-file, and connected-DMS choices used by agent chat. */
export function MatterAssetFileSourceMenu({ matterId, onUpload, onOpenFilePicker }: MatterAssetFileSourceMenuProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const { userAccountData } = useContext(AuthContext)
  const externalStorageAction = useExternalStorageMenuStatus({ matterId, menuOpen: open })

  const handleConnectFolder = () => {
    setOpen(false)
    if (!matterId) return
    trackMatterDriveSyncEvent(
      AnalyticsEvent.MatterDriveSyncSetupEntryClicked,
      {
        matterId,
        organizationId: getDriveSyncOrganizationId(userAccountData),
        matterDriveSyncEntrySource: MatterDriveSyncEntrySource.MatterAssetAddFiles,
        ...(externalStorageAction.displayProvider ? { externalStorageProvider: externalStorageAction.displayProvider } : {}),
      },
      trackEvent
    )
    navigate(matterPaths.settings(matterId))
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-brand-neutral-400 bg-brand-neutral-200 hover:bg-brand-neutral-300 text-sm font-semibold tracking-[-0.15px] text-brand-neutral-800 transition-colors"
        >
          Choose Files
        </button>
      </DropdownMenu.Trigger>
      {!externalStorageAction.loading && (
        <MenuContent className="w-max max-w-[min(360px,calc(100vw-32px))]" align="start" sideOffset={4} collisionPadding={16}>
          <MenuItemGroup>
            <MenuItem label="Upload from computer" icon={Upload} variant="neutral" onSelect={onUpload} />
            <MenuItem label="Add from matter files" icon={Folder} variant="neutral" onSelect={() => onOpenFilePicker(ADD_FILES_MODAL_TAB.MATTER_FILES)} />
            <ExternalStorageMenuItems
              status={externalStorageAction}
              onOpenExternalFiles={() => onOpenFilePicker(ADD_FILES_MODAL_TAB.EXTERNAL_FILES)}
              onConnectFolder={handleConnectFolder}
            />
          </MenuItemGroup>
          <FilePickerSecurityFooter variant={externalStorageAction.footerVariant} onNavigate={() => setOpen(false)} />
        </MenuContent>
      )}
    </DropdownMenu.Root>
  )
}
